import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import type { Permission, SessionUser } from '@denwen-msp/shared';
import { ApiError, api, onUnauthorized } from '@/lib/api';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  can: (p: Permission) => boolean;
  setUser: (u: SessionUser | null) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);
const ME = ['auth', 'me'];

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ME,
    queryFn: async () => {
      try {
        return await api<SessionUser>('/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  // Any 401 from any request (expired session, revoked device) drops back to sign-in.
  useEffect(() => onUnauthorized(() => qc.setQueryData(ME, null)), [qc]);

  const user = me.data ?? null;
  const value: AuthState = {
    user,
    loading: me.isLoading,
    can: (p) => Boolean(user?.permissions.includes(p)),
    setUser: (u) => qc.setQueryData(ME, u),
    signOut: async () => {
      await api('/auth/logout', { method: 'POST' }).catch(() => {});
      // Signed out first, so the app leaves the signed-in pages; then everything else is dropped.
      // (qc.clear() first would detach this provider from the "me" query and keep showing the old
      // user, while every page refetched into "Sign in to continue".)
      await qc.cancelQueries();
      qc.setQueryData(ME, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== ME[0] || q.queryKey[1] !== ME[1] });
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** The auth state, or null outside AuthProvider (shared UI that also renders in isolation). */
export const useOptionalAuth = (): AuthState | null => useContext(AuthContext);

/** The signed-in user (only use inside authenticated routes). */
export function useUser(): SessionUser {
  const { user } = useAuth();
  if (!user) throw new Error('useUser without a signed-in user');
  return user;
}

/** Renders children only when the user holds the permission. */
export function Can({ permission, children, fallback = null }: { permission: Permission; children: ReactNode; fallback?: ReactNode }) {
  const { can } = useAuth();
  return <>{can(permission) ? children : fallback}</>;
}
